import { useContext, useEffect, useMemo, useState } from 'react';

import Card from '@/components/Card';
import CreateStateModal from '@/modals/CreateStateModal';
import { DEFAULT_PAGE_SIZE } from '@/constants';
import { GlobalContext } from '../../../context';
import Input from '@/components/Inputs';
import { Link } from 'react-router-dom';
import LoadingOverlay from '@/components/LoadingOverlay';
import Table from '@/components/Table';
import { prepareTableConfig } from '../../../utils';
import { statesService } from '@/services';
import { useRouteMatch } from 'react-router';

const CONFIG = {
  name: 'States',
  widgets: [],
};

let queryDebounceTimer = null;

const SuperAdminStates = () => {
  const {
    currentPlatform: { uid: platformId },
  } = useContext(GlobalContext);
  const [loading, setLoading] = useState(true);
  const { url } = useRouteMatch();

  const [states, setStates] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [tableHeaders, setTableHeaders] = useState([]);
  const [pageCount, setPageCount] = useState(1);
  const [tableData, setTableData] = useState([]);
  const [showAddState, setShowAddState] = useState(false);
  const [paginatedData, setPaginatedData] = useState();
  const [statesInfo, setStatesInfo] = useState([]);
  const memoizedTableHeaders = useMemo(() => tableHeaders, [tableHeaders]);
  const memoizedTableData = useMemo(() => tableData, [tableData]);

  const [search, setSearch] = useState('');

  useEffect(() => {
    let isUnmounted = false;

    if (!isUnmounted) {
      const { tHeaders, tData } = prepareTableConfig(paginatedData, `states`);

      tHeaders.push({
        Header: () => null,
        id: 'edit',
        // eslint-disable-next-line react/display-name
        Cell: ({
          row: {
            original: {
              rawData: { uid },
            },
          },
        }) => (
          <Link
            to={`${url}/${uid}`}
            className="font-semibold text-mmj-blue underline"
          >
            edit
          </Link>
        ),
      });

      setTableHeaders(tHeaders);
      setTableData(tData);
    }

    return () => {
      isUnmounted = true;
    };
  }, [paginatedData]);

  useEffect(() => {
    loadStates({
      name: search,
    });

    return () => {
      if (queryDebounceTimer) {
        clearTimeout(queryDebounceTimer);
      }
    };
  }, []);

  useEffect(() => {
    setPaginatedData(
      states.slice(
        (currentPage - 1) * DEFAULT_PAGE_SIZE,
        currentPage * DEFAULT_PAGE_SIZE,
      ),
    );
  }, [currentPage, states]);

  const loadStates = async (params) => {
    setLoading(true);
    try {
      const { data } = await statesService.getAllStates({
        ...params,
      });
      setPageCount(Math.ceil(data.length / DEFAULT_PAGE_SIZE));
      setStates(data);
      await loadAvailableStates();
    } catch (err) {
      console.log(err);
    }

    setLoading(false);
  };

  const loadAvailableStates = async () => {
    const statesInfo = await statesService.getAvailableStateInfos();
    setStatesInfo(statesInfo);
  };

  const onPageChange = (page) => {
    setCurrentPage(page);
  };

  const handleAddState = () => {
    setShowAddState(true);
  };

  const closeAddStateModal = () => {
    setShowAddState(false);
  };

  const onSearchChanged = (e) => {
    setSearch(e.target.value);
    if (queryDebounceTimer) {
      clearTimeout(queryDebounceTimer);
    }

    queryDebounceTimer = setTimeout(() => {
      loadStates({
        name: e.target.value,
      });
    }, 300);
  };

  const onAddState = async (stateInfo) => {
    try {
      setLoading(true);
      closeAddStateModal();
      const payload = { ...stateInfo };

      if (!payload.state_id_issue_url) {
        delete payload.state_id_issue_url;
      }

      if (payload.state_provider_requirements) {
        payload.state_provider_requirements =
          payload.state_provider_requirements.split('\n');
      } else {
        payload.state_provider_requirements = [];
      }

      if (payload.state_patient_requirements) {
        payload.state_patient_requirements =
          payload.state_patient_requirements.split('\n');
      } else {
        payload.state_patient_requirements = [];
      }

      await statesService.createState(platformId, payload);
      loadStates({
        name: search,
      });
      await loadAvailableStates();
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <Card>
      {loading && <LoadingOverlay show={loading} />}
      <div className="flex justify-between items-center mb-4">
        <div className="text-lg font-semibold">All {CONFIG.name}</div>
        <div className="flex space-x-2 items-center">
          <Input
            type="text"
            placeholder="Search..."
            onChange={onSearchChanged}
            id="payments-search"
            className="mb-0"
          />
          <button
            className="button button-primary"
            type="button"
            onClick={handleAddState}
          >
            Add
          </button>
        </div>
      </div>
      <Table
        data={memoizedTableData}
        columns={memoizedTableHeaders}
        onPageChange={onPageChange}
        pageCount={pageCount}
        enablePagaination
      />
      <CreateStateModal
        show={showAddState}
        onClose={closeAddStateModal}
        onSave={onAddState}
        availableStates={statesInfo}
      />
    </Card>
  );
};

export default SuperAdminStates;
